import React, { useState, useEffect, useRef } from 'react';
import { X, Heart, Plus, Phone, Mail, Globe, Info, BriefcaseBusiness, GalleryThumbnails, Scissors, Shirt, MapPin, Tag, ChevronRight, MessageSquareText, Video, ChevronDown, RulerIcon } from 'lucide-react';
import { createPortal } from 'react-dom';
import userService from '../services/UserService';
import { User } from './UserModal';
import { useIsMobile } from '../submission_boards/component/useIsMobile';
import SbProfileSelector from '../submission_boards/SbProfileSelector';
import { showToast } from '../shared/Toast';
import { getProfileHeightString, getProfileWeightString, humanize, isUndefinedNullEmpty, SbCurrentUser } from '../submission_boards/component/SbModel';
import imdb_logo from '../../assets/images/imdb_logo.svg'
import PhoneNumber from '../shared/PhoneNumber';
interface ProfileDetailPopupProps {
  user?: User;
  currentUser: SbCurrentUser;
  userUuid?: string;
  project_id?: string;
  project_folder_id?: string;
  link_from?: string;
  sb_id?: string;

  onClose: () => void;
}


const KitCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
}> = ({ icon, title, description }) => (
  <div
    className="border-blue-503 bg-blue-502-40 rounded-2xl gap-8 px-6 py-6  flex flex-col h-full border cursor-pointer transition-shadow hover:shadow-md"
    style={{
      minWidth: '9rem'
    }}
  >
    <div className="">
      {icon}
    </div>
    <div className="flex flex-col gap-2">
      <h3 className="font-semibold text-base primary-color" style={{}}>{title}</h3>
      <p className="text-xs leading-relaxed break-words" style={{}}>{description}</p>
    </div>
  </div>
);

const MobileKitRow: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
}> = ({ icon, title, description }) => (
  <div
    className="bg-blue-502-40 border-blue-503 rounded-2xl px-6 py-6 flex items-center justify-between border cursor-pointer transition-shadow hover:shadow-md h-14"
    style={{}}
  >
    <div className="flex items-center gap-3">
      <div
        className="h-10 w-10 rounded-full flex items-center justify-center"
      // style={{ backgroundColor: '#FFFFFF', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}
      >
        {icon}
      </div>
      <div>
        <div className="font-semibold text-base" style={{ color: '#2D3A4B' }}>{title}</div>
        <div className="hidden sm:flex text-xs" style={{ color: '#6B7A8F' }}>{description}</div>
      </div>
    </div>
    <ChevronRight className="h-5 w-5" style={{ color: '#6B7A8F' }} />
  </div>
);


export const ProfileDetailPopup: React.FC<ProfileDetailPopupProps> = ({ user, currentUser, userUuid, onClose, project_id, project_folder_id,
  link_from, sb_id
}) => {
  const [padding, setPadding] = useState({ top: '100px', bottom: '16px' });
  const [userSelected, setUserSelected] = useState<User | null>(null);
  const [showModal, setshowModal] = useState<boolean>(true);
  const [isContactExpanded, setIsContactExpanded] = useState<boolean>(false);
  const isMobile = useIsMobile();
  const [showAddToSb, setshowAddToSb] = useState(false);
  const [showAddTooltip, setShowAddTooltip] = useState(false);
  const [showFavTooltip, setShowFavTooltip] = useState(false);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  useEffect(() => {
    fetchUserProfile(userUuid);
  }, [userUuid]);

  const fetchUserProfile = async (_userUuid: string) => {
    if (_userUuid != undefined) {
      const userData = await userService.getUserProfile(_userUuid, project_id, project_folder_id);
      if (userData != null && 'user' in userData) {
        if (userData.user.first_name == null) {
          closeHandler();
          return;
        }
        setUserSelected(userData.user);
      }
    } else if (user != undefined) {
      if (user.first_name == null) {
        closeHandler();
        return;
      }
      setUserSelected(user);

    }
  }

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    if (!isMobile) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      if (!isMobile) {
        document.body.style.overflow = originalOverflow;
      }
    };
  }, [isMobile]);

  useEffect(() => {
    const updateDimensions = () => {
      if (window.innerWidth >= 768) {
        // Desktop: Account for fixed navbar (64px) + breadcrumb (48px) + spacing
        setPadding({ top: '112px', bottom: '32px' });
      } else {
        setPadding({ top: '80px', bottom: '24px' });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);

    return () => {
      window.removeEventListener('resize', updateDimensions);
    };
  }, []);
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeHandler();
      }
    };
    if (showModal) {
      window.addEventListener('keydown', handleEsc);
    }
    return () => window.removeEventListener('keydown', handleEsc);
  }, [showModal]);

  const isClosing = useRef(false);

  useEffect(() => {
    const handlePopState = (e: PopStateEvent) => {
      if (!isClosing.current) {
        isClosing.current = true;
        setUserSelected(null);
        setshowModal(false);
        onClose();
      }
    };
    if (showModal) {
      window.history.pushState({ modalOpen: true }, '');
      window.addEventListener('popstate', handlePopState);
    }
    return () => window.removeEventListener('popstate', handlePopState);
  }, [showModal]);

  const closeHandler = () => {
    if (isClosing.current) return;
    isClosing.current = true;

    if (window.history.state?.modalOpen) {
      window.history.back();
    }
    setUserSelected(null);
    setshowModal(false)
    onClose();
  }
  const handleAddToSubmissionBoard = () => {
    setshowAddToSb(true);
  }
  const handleAddToFav = async () => {
    try {
      const resp = await userService.toggleFavorite(userSelected.uuid, !userSelected.is_fav);
      console.log(resp);
      if (resp) {
        setUserSelected(prev => ({ ...prev, is_fav: !userSelected.is_fav }))
        showToast('Added to favorite.', 'success');
      }
    } catch (error) {
      showToast('Failed to add in favorite.', 'success');
    }
  }

  const isAddress = (address: any) => {
    if (isUndefinedNullEmpty(address?.street_address)
      && isUndefinedNullEmpty(address?.street_address_line2)
      && isUndefinedNullEmpty(address?.city)
      && isUndefinedNullEmpty(address?.state)
      && isUndefinedNullEmpty(address?.country)
      && isUndefinedNullEmpty(address?.postal_code)
    ) {
      return false;
    }

    return true;
  }
  const formatPhoneNumber = (value: string) => {
    if (!value) return value;
    const phoneNumber = value.replace(/\D/g, '');
    const phoneNumberLength = phoneNumber.length;
    if (phoneNumberLength < 4) return phoneNumber;
    if (phoneNumberLength < 7) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    }
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  }

  const getAddress = (address: any) => {
    if (
      // isUndefinedNullEmpty(address.street_address)
      // && isUndefinedNullEmpty(address.street_address_line2)
      isUndefinedNullEmpty(address?.city)
      && isUndefinedNullEmpty(address?.state)
      && isUndefinedNullEmpty(address?.country)
      // && isUndefinedNullEmpty(address.postal_code)
    ) {
      return <>-</>
    }

    return <>
      {/* {address.street_address && <>{address.street_address}<br/></>} */}
      {/* {address.street_address_line2 && <>{address.street_address_line2}<br/></>} */}
      {address?.city && <>{address?.city}, </>}
      {address?.state && <>{address?.state}, </>}
      {address?.country && <>{address?.country}</>}
      {/* {address.postal_code && <>{address.postal_code}<br/></>} */}
    </>;
  }
  const getLink = (params: string) => {
    let link = userSelected.link;
    if (link_from == 'sb') {
      link = link.replace('?from=directory', '').replace('from=directory', '')
      params = params + '&from=sb&sb_id=' + sb_id;
      if (project_id != undefined && project_id != '') {
        params = params + '&project_id=' + project_id;

      }
    }
    if (link.indexOf("?") > 0) {
      return link + "&" + params;
    }
    return link + "?" + params;
  }
  return createPortal(
    <>
      {showModal &&
        <div
          className="fixed inset-0 flex items-center justify-center overflow-auto px-4 md:px-10 bg-gray-900 bg-opacity-70"
          style={{
            zIndex: 60,
            paddingTop: padding.top,
            paddingBottom: padding.bottom
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              closeHandler();
            }
          }}
        >
          <div
            className="relative w-full bg-white max-h-full overflow-y-scroll"
            style={{
              maxWidth: '57.5rem',
              borderRadius: '1.25rem',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              animation: 'popupIn 0.2s ease-out'
            }}
          >
            <style>{`
          @keyframes popupIn {
            from { opacity: 0; transform: scale(0.95); }
            to { opacity: 1; transform: scale(1); }
          }
        `}</style>

            {/* Close Button */}
            <button
              onClick={closeHandler}
              type="button"
              className="absolute right-3 top-3 z-10 h-8 w-8 rounded-full flex items-center justify-center transition-colors"
              style={{ backgroundColor: 'rgba(0,0,0,0.05)', color: '#6B7A8F' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.1)';
                e.currentTarget.style.color = '#2D3A4B';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.05)';
                e.currentTarget.style.color = '#6B7A8F';
              }}
            >
              <X className="h-4 w-4" />
            </button>

            {userSelected != null && <div className="flex flex-col "
              style={{
                // gap:'1.875rem',
                padding: isMobile ? '1rem' : '2.5rem',
                paddingTop: isMobile ? '3rem' : '2.5rem'

              }}>
              {/* Header Section */}
              <div className="flex flex-col gap-5">
                <div className="flex flex-col md:flex-row gap-6">
                  {/* Row 1: Profile Image and User Info - Mobile: side by side, Desktop: as before */}
                  <div className={`flex flex-1 ${isMobile ? 'flex-row items-start gap-4' : 'flex-col md:flex-row gap-4 md:gap-6'}`}>
                    {/* Profile Image */}
                    <div className="flex-shrink-0 flex md:block items-center gap-4">
                      <img
                        src={userSelected.avatar_url?.startsWith('icons/') ? `/assets/${userSelected.avatar_url}` : (userSelected.cropped_avatar_url || userSelected.avatar_url || '/assets/icons/default_avatar.svg')}
                        alt={(userSelected.first_name?.charAt(0)?.toUpperCase() + userSelected.first_name?.slice(1)?.toLowerCase()) + ' ' + (userSelected.last_name?.charAt(0)?.toUpperCase() + userSelected.last_name?.slice(1)?.toLowerCase())}
                        className={isMobile
                          ? `rounded-lg ${(!userSelected.avatar_url || userSelected.avatar_url?.startsWith('icons/')) ? 'object-contain' : 'object-cover'}`
                          : `rounded-2xl ${(!userSelected.avatar_url || userSelected.avatar_url?.startsWith('icons/')) ? 'object-contain' : 'object-cover'}`}
                        style={isMobile ? {
                          width: '7.5rem',
                          height: '9.375rem',
                          backgroundColor: '#E8EDF4',
                          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                        } : {
                          width: '11rem',
                          height: '13.75rem',
                          backgroundColor: '#E8EDF4',
                          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                        }}
                      />
                    </div>

                    {/* User Info */}
                    <div className="flex-grow flex flex-col gap-3">
                      {/* Mobile: PRO badge */}

                      <div className="flex flex-col items-start justify-start w-full">
                        {isMobile && <div
                          className='flex items-center gap-2'>

                          {userSelected?.pro_or_higher && <span
                            className="flex sm:hidden uppercase tracking-wider bg-black text-white text-xs font-semibold px-2 py-1 rounded-full"
                            style={{ maxWidth: '3rem' }}
                          >
                            PRO
                          </span>}
                          {userSelected && currentUser?.id && userSelected.currentuser_is_fav_allowed && !userSelected.is_prod_support_onboarding && <button
                            className="h-6 w-6 rounded-full flex items-center justify-center transition-colors border-0 bg-transparent"

                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F5F7FA'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            onClick={() => {
                              handleAddToFav();
                            }}
                          >
                            <Heart className="h-6 w-6" style={{
                              fill: (userSelected.is_fav ? '#8F79F1' : '#ffffff')
                            }} />
                          </button>}
                        </div>}
                        <div className="flex items-center gap-3">
                          <div
                            className={isMobile ? "text-2xl font-semibold tracking-tight" : "text-3xl md:text-4xl font-semibold tracking-tight"}
                            style={{ color: '#2D3A4B' }}
                          >
                            {userSelected?.first_name != null && userSelected?.last_name != null && (userSelected.first_name?.charAt(0)?.toUpperCase() + userSelected.first_name?.slice(1)?.toLowerCase()) + ' ' + (userSelected.last_name?.charAt(0)?.toUpperCase() + userSelected.last_name?.slice(1)?.toLowerCase())}
                          </div>
                          {/* Desktop: PRO badge */}
                          {!isMobile && userSelected?.pro_or_higher && <span
                            className="bg-black text-white text-xs font-semibold px-2 py-1 rounded-full uppercase tracking-wider"
                            style={{ color: '#FFFFFF' }}
                          >
                            PRO
                          </span>}

                        </div>
                        <p
                          className={isMobile ? "text-base font-medium" : "text-lg md:text-xl font-medium"}
                          style={{ color: '#576C89', minHeight: '1.75rem' }}
                        >
                          {(userSelected.role_name || userSelected.role || " ").replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                        </p>
                      </div>

                      <div className="space-y-2 flex flex-col" style={{ color: '#6B7A8F' }}>
                        {(isAddress(userSelected) || (userSelected?.secondary_location && isAddress(userSelected?.secondary_location))) && (
                          <div className="flex items-start gap-2">
                            <MapPin className="hidden sm:flex h-4 w-4 shrink-0 mt-0.5" />
                            <span className="text-sm flex flex-col sm:flex-row sm:items-center flex-1">
                              <span>{isAddress(userSelected) && getAddress(userSelected)}</span>
                              {isAddress(userSelected) && userSelected?.secondary_location && isAddress(userSelected?.secondary_location) && <span className="hidden sm:inline mx-1">, • </span>}
                              <span>{userSelected?.secondary_location && isAddress(userSelected?.secondary_location) && getAddress(userSelected?.secondary_location)}</span>
                            </span>
                          </div>
                        )}
                        {(userSelected.gender || userSelected.height || userSelected.weight) && (
                          <div className="flex items-center gap-2">
                            <RulerIcon className="hidden sm:flex h-4 w-4" />
                            <span className="text-sm">
                              {[humanize(userSelected.gender), getProfileHeightString(userSelected, currentUser),
                              getProfileWeightString(userSelected, currentUser)].filter(Boolean).join(' • ')}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Action Buttons - Desktop: Heart/Plus */}
                      {userSelected && !userSelected.is_prod_support_onboarding && !isMobile && (
                        <div className="flex gap-3 mt-2">
                          {currentUser?.id && userSelected.currentuser_is_fav_allowed && (
                            <div
                              className="relative"
                              onMouseEnter={() => setShowFavTooltip(true)}
                              onMouseLeave={() => setShowFavTooltip(false)}
                            >
                              <button
                                className="h-10 w-10 rounded-full flex items-center justify-center transition-colors"
                                style={{ border: '0.0625rem solid #D1D9E6', color: '#6B7A8F' }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F5F7FA'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                onClick={() => {
                                  handleAddToFav();
                                }}
                              >
                                <Heart className="h-4 w-4" style={{
                                  fill: (userSelected.is_fav ? '#8F79F1' : '#ffffff')
                                }} />
                              </button>
                              {showFavTooltip && (
                                <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 flex flex-col items-center z-50">
                                  <div className="flex flex-row justify-center items-center relative text-xs font-medium leading-none text-white whitespace-nowrap" style={{ background: '#0D1621', border: '0.0625rem solid #0D1621', borderRadius: '999px', padding: '0.875rem 1rem', height: '2.5rem' }}>
                                    {userSelected.is_fav ? 'Remove Favorite' : 'Add Favorite'}
                                    <div className="absolute" style={{ width: '1rem', height: '1rem', left: 'calc(50% - 1rem/2)', top: '-0.4375rem', background: '#0D1621', borderRadius: '0.125rem', transform: 'rotate(45deg)', zIndex: 1 }} />
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                          {currentUser?.id && userSelected.show_add_sb_button && (
                            <div
                              className="relative"
                              onMouseEnter={() => setShowAddTooltip(true)}
                              onMouseLeave={() => setShowAddTooltip(false)}
                            >
                              <button
                                className="h-10 w-10 rounded-full flex items-center justify-center transition-colors"
                                style={{ border: '0.0625rem solid #D1D9E6', color: '#6B7A8F' }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F5F7FA'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                onClick={() => {
                                  handleAddToSubmissionBoard();
                                }}
                              >
                                <Plus className="h-4 w-4" />
                              </button>
                              {showAddTooltip && (
                                <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 flex flex-col items-center z-50">
                                  <div className="flex flex-row justify-center items-center relative text-xs font-medium leading-none text-white whitespace-nowrap" style={{ background: '#0D1621', border: '0.0625rem solid #0D1621', borderRadius: '999px', padding: '0.875rem 1rem', height: '2.5rem' }}>
                                    Add to Submission Board
                                    <div className="absolute" style={{ width: '1rem', height: '1rem', left: 'calc(50% - 1rem/2)', top: '-0.4375rem', background: '#0D1621', borderRadius: '0.125rem', transform: 'rotate(45deg)', zIndex: 1 }} />
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Row 2: Mobile Action Buttons - Call/Email/Text/FaceTime */}
                {isMobile && (
                  <div className="flex flex-row gap-2 mb-4">
                    <a
                      href={userSelected?.phone ? `tel:${userSelected.phone.replace(/[-\s]/g, '')}` : '#'}
                      className={" flex-1 " + (userSelected?.phone ? 'flex' : 'pointer-events-none opacity-50')}
                      aria-label={userSelected?.phone ? `Call ${userSelected.phone}` : 'No phone provided'}
                    >
                      <button
                        className="flex flex-col w-full items-center justify-center gap-1 px-3 py-3 rounded-lg transition-colors"
                        style={{ border: '0.0625rem solid #8CA2C2', color: '#2D3A4B' }}
                        onMouseEnter={(e) => userSelected?.phone && (e.currentTarget.style.backgroundColor = '#F5F7FA')}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        disabled={!userSelected?.phone}
                      >
                        <Phone className="h-5 w-5" />
                        <span className="text-xs font-medium">Call</span>
                      </button>
                    </a>
                    <a
                      href={`mailto:${userSelected?.email}`}
                      className=" flex-1 flex"
                      aria-label={`Mail ${userSelected?.email}`}
                    >
                      <button
                        className="flex flex-col w-full items-center justify-center gap-1 px-3 py-3 rounded-lg transition-colors"
                        style={{ border: '0.0625rem solid #8CA2C2', color: '#2D3A4B' }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F5F7FA'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <Mail className="h-5 w-5" />
                        <span className="text-xs font-medium">Email</span>
                      </button>
                    </a>
                    <a
                      href={userSelected?.phone ? `sms:${userSelected.phone.replace(/[-\s]/g, '')}` : '#'}
                      className={" flex-1 " + (userSelected?.phone ? 'flex' : 'pointer-events-none opacity-50')}
                      aria-label={userSelected?.phone ? `Text ${userSelected.phone}` : 'No phone provided'}
                    >
                      <button
                        className="flex flex-col w-full items-center justify-center gap-1 px-3 py-3 rounded-lg transition-colors"
                        style={{ border: '0.0625rem solid #8CA2C2', color: '#2D3A4B' }}
                        onMouseEnter={(e) => userSelected?.phone && (e.currentTarget.style.backgroundColor = '#F5F7FA')}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        disabled={!userSelected?.phone}
                      >
                        <MessageSquareText className="h-5 w-5" />
                        <span className="text-xs font-medium">Text</span>
                      </button>
                    </a>
                    {isIOS && (
                      <a
                        href={userSelected?.phone ? `facetime://${userSelected.phone.replace(/[-\s]/g, '')}` : '#'}
                        className={" flex-1 " + (userSelected?.phone ? 'flex' : 'pointer-events-none opacity-50')}
                        aria-label={userSelected?.phone ? `FaceTime ${userSelected.phone}` : 'No phone provided'}
                      >
                        <button
                          className="flex flex-col w-full items-center justify-center gap-1 px-3 py-3 rounded-lg transition-colors"
                          style={{ border: '0.0625rem solid #8CA2C2', color: '#2D3A4B' }}
                          onMouseEnter={(e) => userSelected?.phone && (e.currentTarget.style.backgroundColor = '#F5F7FA')}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          disabled={!userSelected?.phone}
                        >
                          <Video className="h-5 w-5" />
                          <span className="text-xs font-medium">FaceTime</span>
                        </button>
                      </a>
                    )}
                  </div>
                )}

                {/* Contact & Links Grid */}
                <div
                  className={`rounded-lg overflow-hidden mt-0 ${isMobile ? 'flex flex-col-reverse' : ''}`}>
                  {/* Mobile: Collapsible Header */}
                  {isMobile && (
                    <div
                      className="flex flex-row justify-center items-center w-full"
                      style={{
                        height: '28px',
                        padding: '0px',
                        gap: '10px'
                      }}
                    >
                      {/* Left line */}
                      <div
                        style={{
                          height: '0px',
                          border: '0.0625rem solid #C5D1E1',
                          flex: '1 1 0'
                        }}
                      />

                      {/* Circular button */}
                      <button
                        onClick={() => setIsContactExpanded(!isContactExpanded)}
                        className="flex flex-row justify-center items-center flex-none"
                        style={{
                          width: '28px',
                          height: '28px',
                          padding: '0px',
                          background: '#FFFFFF',
                          borderRadius: '999px',
                          boxSizing: 'border-box',
                          border: '1px solid #3C82F6',
                        }}
                      >
                        <ChevronDown
                          className="transition-transform duration-200"
                          style={{
                            width: '20px',
                            height: '20px',
                            color: '#576C89',
                            transform: isContactExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
                          }}
                          strokeWidth={1.5}
                        />
                      </button>

                      {/* Right line */}
                      <div
                        style={{
                          height: '0px',
                          border: '0.0625rem solid #C5D1E1',
                          flex: '1 1 0'
                        }}
                      />
                    </div>
                  )}
                  <div
                    className={`transition-all duration-300 ease-in-out ${isMobile ? (isContactExpanded ? 'max-h-1000 opacity-100 pb-3.5 opacity-100' : 'max-h-0 opacity-0 overflow-hidden') : ''}`}
                  >
                    <div
                      className="grid grid-cols-1 md:grid-cols-4 overflow-hidden px-3 pt-3 pb-2 border border-gray-300 rounded-lg gap-2"
                    >
                      {/* Phone */}
                      <div className="flex flex-row sm:flex-col gap-2 pt-1 pb-3 sm:py-1 border-b sm:border-b-0">
                        <div className={'flex flex-row h-9 ' +
                          (isMobile ? ' w-14 ' : ' w-9 ')
                        }>
                          <Phone className={`h-5 w-5 ${!userSelected?.phone ? 'opacity-30' : ''}`} style={{ color: '#576C89' }} />
                        </div>
                        <div className='flex flex-col'>
                          <div
                            className="text-xs font-semibold tracking-wider"
                            style={{ color: '#576C89' }}
                          >
                            Phone(s)
                          </div>
                          {userSelected?.phone ? (
                            <a
                              href={`tel:${userSelected.phone.replace(/[-\s]/g, '')}`}
                              className="flex"
                              aria-label={`Call ${userSelected.phone}`}
                            >
                              <div
                                className="text-sm font-medium break-words whitespace-pre-line"
                                style={{ color: '#0D1621' }}
                              >
                                <PhoneNumber
                                  phone={userSelected.phone}
                                  country={userSelected.country}
                                  country_code={userSelected.country_code}
                                  showIcon={false}
                                />
                              </div>
                            </a>
                          ) : (
                            <div className="h-4 w-28 bg-gray-200 rounded-md animate-pulse mt-1 mb-1"></div>
                          )}
                        </div>
                      </div>

                      {/* Email */}
                      <div
                        className="flex flex-row sm:flex-col gap-2 pt-1 pb-3 sm:py-1  border-b sm:border-b-0 md:border-l sm:px-4"
                        style={{ borderColor: '#D1D9E6' }}
                      >
                        <div className={'flex flex-row h-9 ' +
                          (isMobile ? ' w-14 ' : ' w-9 ')
                        }>
                          <Mail className="h-5 w-5" style={{ color: '#576C89' }} />
                        </div>
                        <div className='flex flex-col'>
                          <div
                            className="text-xs font-semibold tracking-wider"
                            style={{ color: '#576C89' }}
                          >
                            Email
                          </div>
                          <a
                            href={`mailto:${userSelected?.email}`}
                            className="flex items-center"
                            aria-label={`Mail ${userSelected?.email}`}
                          >
                            <div
                              className="text-sm font-medium break-all"
                              style={{ color: '#0D1621' }}
                            >
                              {userSelected.email}
                            </div>
                          </a>
                        </div>
                      </div>

                      {/* IMDb */}
                      {userSelected && !userSelected.is_prod_support_onboarding && (
                        <div
                          className="flex flex-row sm:flex-col gap-2 pt-1 pb-3 sm:py-1  border-b sm:border-b-0 md:border-l sm:px-4"
                          style={{ borderColor: '#D1D9E6' }}
                        >
                          <div className={'flex flex-row h-9 ' +
                            (isMobile ? ' w-14 ' : ' w-9 ')
                          }>
                            <img className={`mr-auto h-4 object-cover ${!userSelected.imdb_url ? 'opacity-30 grayscale' : ''}`} src={imdb_logo} alt="" />
                          </div>
                          <div className='flex flex-col'>
                            <div
                              className="text-xs font-semibold tracking-wider"
                              style={{ color: '#576C89' }}
                            >
                              IMDb Profile
                            </div>
                            {userSelected.imdb_url ? (
                              <a
                                href={userSelected.imdb_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center w-full"
                                style={{ color: '#0D1621' }}
                              >
                                <div
                                  className="text-sm font-medium w-full overflow-hidden"
                                  style={{
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    wordBreak: 'break-all',
                                    lineHeight: '1.5',
                                  }}
                                >
                                  {userSelected.imdb_url.replace(/^https?:\/\//, '')}
                                </div>
                              </a>
                            ) : (
                              <div className="h-4 w-28 bg-gray-200 rounded-md animate-pulse mt-1 mb-1"></div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Website */}
                      {userSelected && !userSelected.is_prod_support_onboarding && (
                        <div
                          className="flex flex-row sm:flex-col gap-2 pt-1 pb-3 sm:py-1 md:border-l sm:px-4"
                          style={{ borderColor: '#D1D9E6' }}
                        >
                          <div className={'flex flex-row h-9 ' +
                            (isMobile ? ' w-14 ' : ' w-9 ')
                          }>
                            <Globe className={`h-5 w-5 ${!userSelected.website ? 'opacity-30' : ''}`} style={{ color: '#576C89' }} />
                          </div>
                          <div className='flex flex-col'>
                            <div
                              className="text-xs font-semibold tracking-wider"
                              style={{ color: '#576C89' }}
                            >
                              Website
                            </div>
                            {userSelected.website ? (
                              <a
                                href={userSelected.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center w-full"
                                style={{ color: '#0D1621' }}
                              >
                                <div
                                  className="text-sm font-medium w-full overflow-hidden"
                                  style={{
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    wordBreak: 'break-all',
                                    lineHeight: '1.5',
                                  }}
                                >
                                  {userSelected.website.replace(/^https?:\/\//, '')}
                                </div>
                              </a>
                            ) : (
                              <div className="h-4 w-28 bg-gray-200 rounded-md animate-pulse mt-1 mb-1"></div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {/* Kits Grid – Desktop */}
              {userSelected && !userSelected.is_prod_support_onboarding && <div className="hidden gap-2 sm:flex flex-col sm:flex-row" style={{ paddingTop: '1.875rem', height: '13.75rem' }}>
                <a href={getLink("tab=about")} target='_blank' className="flex-1 min-w-0" style={{
                  minWidth: '9rem'
                }}>
                  <KitCard
                    icon={<Info className="h-5 w-5" style={{ color: '#2D3A4B' }} />}
                    title="About"
                    description={userSelected.general_information ? userSelected.general_information.slice(0, 28) + '...' : 'No bio available'}
                  />
                </a>
                {userSelected.work_kit && (
                  <a href={getLink("tab=work_kits")} target='_blank' className="flex-1 min-w-0" style={{
                    minWidth: '9rem'
                  }}>
                    <KitCard
                      icon={<BriefcaseBusiness className="h-5 w-5" style={{ color: '#2D3A4B' }} />}
                      title="Work Kit"
                      description="Legal and travel documents"
                    />
                  </a>
                )}
                {userSelected.submission_kit && (
                  <a href={getLink("tab=submission_kits")} target='_blank' className="flex-1 min-w-0" style={{
                    minWidth: '9rem'
                  }}>
                    <KitCard
                      icon={<GalleryThumbnails className="h-5 w-5" style={{ color: '#2D3A4B' }} />}
                      title="Submission Kit"
                      description="Photos, videos and links"
                    />
                  </a>
                )}
                {userSelected.hair_make_up_kit && (
                  <a href={getLink("tab=hair_make_up_kits")} target='_blank' className="flex-1 min-w-0" style={{
                    minWidth: '9rem'
                  }}>
                    <KitCard
                      icon={<Scissors className="h-5 w-5" style={{ color: '#2D3A4B' }} />}
                      title="Hair & Makeup Kit"
                      description="Current look and special features"
                    /></a>
                )}
                {userSelected.wardrobe_kit && (
                  <a href={getLink("tab=wardrobe_kits")} target='_blank' className="flex-1 min-w-0" style={{
                    minWidth: '9rem'
                  }}>
                    <KitCard
                      icon={<Shirt className="h-5 w-5" style={{ color: '#2D3A4B' }} />}
                      title="Wardrobe Kit"
                      description="Full wardrobe profile"
                    />
                  </a>
                )}
              </div>}

              {/* Kits List – Mobile */}
              {userSelected && !userSelected.is_prod_support_onboarding && <div className="md:hidden flex flex-col gap-2-1 pt-7">
                <a href={getLink("tab=about")} >
                  <MobileKitRow
                    icon={<Info className="h-5 w-5" style={{ color: '#2D3A4B' }} />}
                    title="About"
                    description={userSelected.general_information ? userSelected.general_information.slice(0, 28) + '...' : 'No bio available'}
                  />
                </a>
                {userSelected.work_kit && (
                  <a href={getLink("tab=work_kits")} >
                    <MobileKitRow
                      icon={<BriefcaseBusiness className="h-5 w-5" style={{ color: '#2D3A4B' }} />}
                      title="Work Kit"
                      description="Legal and travel documents"
                    />
                  </a>
                )}
                {userSelected.submission_kit && (
                  <a href={getLink("tab=submission_kits")} >
                    <MobileKitRow
                      icon={<GalleryThumbnails className="h-5 w-5" style={{ color: '#2D3A4B' }} />}
                      title="Submission Kit"
                      description="Photos, videos and links"
                    />
                  </a>
                )}
                {userSelected.hair_make_up_kit && (
                  <a href={getLink("tab=hair_make_up_kits")} >
                    <MobileKitRow
                      icon={<Scissors className="h-5 w-5" style={{ color: '#2D3A4B' }} />}
                      title="Hair & Makeup Kit"
                      description="Current look and special features"
                    />
                  </a>
                )}
                {userSelected.wardrobe_kit && (
                  <a href={getLink("tab=wardrobe_kits")} >
                    <MobileKitRow
                      icon={<Shirt className="h-5 w-5" style={{ color: '#2D3A4B' }} />}
                      title="Wardrobe Kit"
                      description="Full wardrobe profile"
                    />
                  </a>
                )}
              </div>}

              {/* Add to Submission Board Button - Mobile */}
              {userSelected && userSelected.show_add_sb_button && !userSelected.is_prod_support_onboarding && isMobile && (
                <div className="pt-6 pb-4">
                  <button
                    className="w-full flex items-center justify-center gap-2 py-4 px-6 rounded-lg font-medium text-white transition-colors"
                    style={{ backgroundColor: '#2D3A4B' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1a2533'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2D3A4B'}
                    onClick={() => {
                      handleAddToSubmissionBoard();
                    }}
                  >
                    <Plus className="h-5 w-5" />
                    <span>Add to Submission Board</span>
                  </button>
                </div>
              )}
            </div>
            }
          </div>
          {showAddToSb && <div className="flex justify-center md:justify-end">
            <SbProfileSelector
              hideButton={true}
              currentUser={null}
              user={userSelected}
              from='sbprofile'
              projects={[]}
              project={null}
              submissionBoardModel={null}
              alreadyInSb={true}
              dialogCloseHandler={function (open: boolean): void {
                setshowAddToSb(false);
              }} />
          </div>}
        </div>
      }</>,
    document.body
  );
};